import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { TableContainer, TableHeader } from '@occc/ui-design-system';
import { DataTable, Row, TableField } from '@occc/ui-design-system';

import { RJRActionsContainer } from './rjr-actions-container';

import { GenericSubmissionGroup } from '../scan-data-submission-groups.types';
import { LoadedDataWithPagination } from '../../../scan-data.types';
import { routeCreator } from '../../../scan-data.router';

type RJRSubmissionGroupsTableProps = LoadedDataWithPagination<GenericSubmissionGroup[]> & {
  showActions: boolean;
};

const headers = [
  { label: 'RJR L1 Act #' },
  { label: 'Locations' },
  { label: 'SFTP Username' },
  { label: 'Submitter ID' },
];

export const RJRSubmissionGroupsTable = ({
  data,
  loading,
  error,
  paginationProps,
  showActions,
}: RJRSubmissionGroupsTableProps) => {
  const navigate = useNavigate();

  const rows = useMemo<Row[] | null>(() => {
    if (!data) return null;
    return data.map((group: GenericSubmissionGroup) => {
      const fields: TableField[] = [
        { label: 'RJR L1 Act #', value: group.title },
        { label: 'Locations', value: group.locations },
        {
          label: 'SFTP Username',
          value: group.sftpUsername,
          sx: { whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' },
        },
        { label: 'Submitter ID', value: group.submitterId },
      ];

      if (showActions) {
        fields.push({
          label: 'Actions',
          value: <RJRActionsContainer id={group.id} />,
          align: 'right',
        });
      }

      return {
        key: group.id,
        fields,
        onClick: () => navigate(routeCreator.submissionGroupsDetails(group.id)),
      };
    });
  }, [data, showActions, navigate]);

  const tableHeaders = showActions ? [...headers, { label: '', align: 'right' as const }] : headers;

  return (
    <TableContainer sx={{ marginBottom: 1 }}>
      <TableHeader title='RJR Submission Groups' />
      <DataTable
        headers={tableHeaders}
        rows={rows}
        loading={loading}
        error={error}
        paginationProps={paginationProps}
      />
    </TableContainer>
  );
};
